/**
 * Campus order name stickers — 12"×18" sheet as direct PDF download.
 * Landscape page: 18" wide × 12" tall · Grid 6×6 = 36 stickers · each 2.5"×1".
 */

import { jsPDF } from "jspdf";
import { normalizeStickerColors } from "./campus_sticker_settings";

/** IITKAN14 → { prefix: "IITKAN", suffix: "14" } */
export function splitInvoiceNo(invoiceNo) {
    const s = String(invoiceNo || "").trim().toUpperCase();
    const m = s.match(/^([A-Z]+)(\d+)$/);
    if (m) return { prefix: m[1], suffix: m[2] };
    return { prefix: s || "—", suffix: "" };
}

/** Sheet: 12×18 paper, landscape → 18" wide × 12" tall */
const SHEET_W_IN = 18;
const SHEET_H_IN = 12;
const COLS = 6;
const ROWS = 6;
const STICKER_W_IN = 2.5;
const STICKER_H_IN = 1;
const PER_PAGE = COLS * ROWS;

const COL_PITCH = SHEET_W_IN / COLS; // 3"
const ROW_PITCH = SHEET_H_IN / ROWS; // 2"

const INV_PT = 0.44 * 72; // ~31.7pt
const NAME_PT = 0.22 * 72; // ~15.8pt

export const STICKER_STATUS_OPTIONS = ["booked", "ready", "delivered", "missing", "cancelled"];

/**
 * MUSTAQBIL delivery rule: order me coat ho to bina coat ke delivery nahi
 * maani jaati. Coat mil gaya aur pant/shirt/tie me se kuchh baaki hai to order
 * DELIVERED hi maana jaata hai — status "missing" dikhta hai, jisme baaki
 * items ki list hoti hai.
 */
export function effectiveStatus(b) {
    const raw = b.status || "booked";
    const items = b.active_items || [];
    const delivered = b.delivered_item_ids || [];
    if (!items.length || !delivered.length) return { status: raw, missing: [] };
    const undelivered = items.filter((i) => !delivered.includes(i.id));
    if (!undelivered.length) return { status: "delivered", missing: [] };
    const coats = items.filter((i) => i.type === "coat");
    const coatOk = coats.length === 0 || coats.every((i) => delivered.includes(i.id));
    if (coatOk) return { status: "missing", missing: undelivered.map((i) => i.name || i.type) };
    return { status: raw === "delivered" || raw === "missing" ? "booked" : raw, missing: [] };
}

/** Status filter match — "missing" bhi delivered hi maana jaata hai. */
export function matchesStatus(b, status) {
    const st = effectiveStatus(b).status;
    if (!status) return true;
    if (status === "delivered") return st === "delivered" || st === "missing";
    return st === status;
}

/** Campus + status filters for sticker PDF (search not applied). */
export function filterBookingsForStickers(bookings, { campus = "", status = "" } = {}) {
    return [...(bookings || [])]
        .filter((b) => (b.invoice_no || "").trim())
        .filter((b) => !campus || b.campus === campus)
        .filter((b) => {
            if (status) return matchesStatus(b, status);
            return effectiveStatus(b).status !== "cancelled";
        });
}

function fitText(doc, text, maxWidthIn) {
    const s = String(text || "").trim() || "—";
    if (doc.getTextWidth(s) <= maxWidthIn) return s;
    let out = s;
    while (out.length > 1 && doc.getTextWidth(`${out}…`) > maxWidthIn) {
        out = out.slice(0, -1);
    }
    return `${out}…`;
}

/**
 * @param {Array} bookings
 * @param {{ campus?: string, status?: string }} [filters]
 * @param {{ background?: string, invoice?: string, invoiceNumber?: string, name?: string }} [colors]
 */
export function generateStickerSheetPDF(bookings, filters = {}, colors = {}) {
    const list = filterBookingsForStickers(bookings, filters)
        .sort((a, b) => String(a.invoice_no || "").localeCompare(String(b.invoice_no || ""), undefined, { numeric: true }));

    const c = normalizeStickerColors(colors);

    if (!list.length) {
        const bits = [];
        if (filters.campus) bits.push(filters.campus);
        if (filters.status) bits.push(filters.status);
        alert(bits.length
            ? `No orders with invoice numbers for: ${bits.join(" · ")}`
            : "No orders with invoice numbers found.");
        return;
    }

    // Exact 18" wide × 12" tall (12×18 sheet landscape).
    // jsPDF: format [12,18] + landscape → pageSize 18×12 (tested).
    const doc = new jsPDF({
        unit: "in",
        format: [SHEET_H_IN, SHEET_W_IN], // [12, 18]
        orientation: "landscape",
        compress: true,
    });

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    if (Math.abs(pageW - SHEET_W_IN) > 0.01 || Math.abs(pageH - SHEET_H_IN) > 0.01) {
        console.warn("sticker PDF page size unexpected:", pageW, pageH, "expected", SHEET_W_IN, SHEET_H_IN);
    }

    const pages = [];
    for (let i = 0; i < list.length; i += PER_PAGE) pages.push(list.slice(i, i + PER_PAGE));

    pages.forEach((chunk, pageIdx) => {
        if (pageIdx > 0) doc.addPage([SHEET_H_IN, SHEET_W_IN], "landscape");

        chunk.forEach((b, i) => {
            const col = i % COLS;
            const row = Math.floor(i / COLS);
            const cellX = col * COL_PITCH;
            const cellY = row * ROW_PITCH;
            const x = cellX + (COL_PITCH - STICKER_W_IN) / 2;
            const y = cellY + (ROW_PITCH - STICKER_H_IN) / 2;

            if (c.background && c.background.toLowerCase() !== "#ffffff") {
                doc.setFillColor(c.background);
                doc.rect(x, y, STICKER_W_IN, STICKER_H_IN, "F");
            }

            const { prefix, suffix } = splitInvoiceNo(b.invoice_no);
            const name = String(b.customer_name || "").trim() || "—";
            const cx = x + STICKER_W_IN / 2;
            const invY = y + STICKER_H_IN * 0.42;
            const nameY = y + STICKER_H_IN * 0.78;

            doc.setFont("helvetica", "bold");
            doc.setFontSize(INV_PT);

            if (suffix) {
                const gap = 0.02;
                const preW = doc.getTextWidth(prefix);
                const sufW = doc.getTextWidth(suffix);
                const totalW = preW + gap + sufW;
                const startX = cx - totalW / 2;
                doc.setTextColor(c.invoice);
                doc.text(prefix, startX, invY, { baseline: "middle" });
                doc.setTextColor(c.invoiceNumber);
                doc.text(suffix, startX + preW + gap, invY, { baseline: "middle" });
            } else {
                doc.setTextColor(c.invoice);
                doc.text(prefix, cx, invY, { align: "center", baseline: "middle" });
            }

            doc.setFont("helvetica", "normal");
            doc.setFontSize(NAME_PT);
            doc.setTextColor(c.name);
            const maxNameW = STICKER_W_IN - 0.12;
            doc.text(fitText(doc, name, maxNameW), cx, nameY, { align: "center", baseline: "middle" });
        });
    });

    const bits = [filters.campus, filters.status].filter(Boolean).join("-").replace(/\s+/g, "_");
    const filename = bits
        ? `campus-stickers-${bits}-${list.length}.pdf`
        : `campus-stickers-${list.length}.pdf`;
    doc.save(filename);
}

export default generateStickerSheetPDF;
